import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTheme } from "../../../contexts/ThemeContext";
import { adminRoutes } from "../../../routers/AdminRouters";
import { useAppSelector } from "../../../redux/hooks";
import { RootState } from "../../../redux/store";
import {
  useCreateSessionMutation,
  useEditSessionMutation,
  useGetCategoriesQuery,
  useGetSessionByIdQuery,
} from "../../../services";
import { toast } from "../../../core/toast";

const SessionForm: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { sessionid } = useParams<{ sessionid?: string }>();
  const { data: categoryData, isError: isCategoryError } = useGetCategoriesQuery();

  const [form, setForm] = useState({
    sessionName: "",
    workoutType: "",
    warmupTime: "00:00:00",
    cooldownTime: "00:00:00",
  });

  const [isSubmitAttempted, setIsSubmitAttempted] = useState(false);
  const [timeErrors, setTimeErrors] = useState<Record<string, string>>({});

  const { data: sessionData, isLoading: isFetching, isError: isSessionError } = useGetSessionByIdQuery(
    sessionid!,
    { skip: !sessionid }
  );

  useEffect(() => {
    if (isCategoryError) {
      toast.error("Failed to load categories.");
    }
  }, [isCategoryError]);

  useEffect(() => {
    if (isSessionError) {
      toast.error("Failed to load session details.");
    }
  }, [isSessionError]);

  const [initialForm, setInitialForm] = useState<any>(null);

  useEffect(() => {
    if (sessionData) {
      const session =
        (sessionData as any)?.result ||
        (sessionData as any)?.data ||
        sessionData;

      const loadedData = {
        sessionName: session?.name || "",
        workoutType: session?.category?._id || session?.category || "",
        warmupTime: session?.warmupTime
          ? secondsToHHMMSS(session.warmupTime)
          : "00:00:00",
        cooldownTime: session?.cooldownTime
          ? secondsToHHMMSS(session.cooldownTime)
          : "00:00:00",
      };

      setForm(loadedData);
      setInitialForm(loadedData);
    }
  }, [sessionData]);

  const cardClass =
    theme === "dark"
      ? "bg-[#0F0F0F] border border-[#2C2C2C] text-gray-100"
      : "bg-white border border-gray-200 text-gray-900";

  const inputClassBase =
    "w-full rounded-md px-3 py-2 outline-none transition-colors";
  const inputClass =
    theme === "dark"
      ? `${inputClassBase} bg-[#111111] border border-[#2C2C2C] placeholder-gray-500 focus:border-[#B6CB24]`
      : `${inputClassBase} bg-white border border-gray-300 placeholder-gray-500 focus:border-[#B6CB24]`;

  const labelClass = theme === "dark" ? "text-[#F3F3F6]" : "text-gray-700";

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const validateTime = (value: string): string | null => {
    if (!value || value.trim() === "" || value === "00:00:00") {
      return "Required";
    }
    const parts = value.split(":");
    if (parts.length !== 3) return "Invalid format (HH:MM:SS)";

    const [hRaw, mRaw, sRaw] = parts;
    const h = parseInt(hRaw);
    const m = parseInt(mRaw);
    const s = parseInt(sRaw);

    if (isNaN(h) || isNaN(m) || isNaN(s)) return "Invalid numbers";
    if (h > 23) return "Hours 0-23";
    if (m > 59) return "Min 0-59";
    if (s > 59) return "Sec 0-59";

    return null;
  };

  const handleTimePartChange =
    (fieldName: "warmupTime" | "cooldownTime", partIdx: number) =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        let val = e.target.value.replace(/\D/g, "");
        if (val.length > 2) {
          val = val.slice(-2);
        }
        
        const parts = (form[fieldName] || "00:00:00").split(":");
        parts[partIdx] = val;
        const newValue = parts.join(":");

        const error = validateTime(newValue);
        setTimeErrors((prev) => {
          if (!error) {
            const { [fieldName]: _, ...rest } = prev;
            return rest;
          }
          return { ...prev, [fieldName]: error };
        });

        setForm((f) => ({ ...f, [fieldName]: newValue }));

        // Auto advance focus only if 2 digits entered and not starting with 0, 
        // or if 2 digits entered and it's a reasonable jump.
        // Actually, removing auto-advance to make it consistent with seconds field 
        // and allow "entire value" entry as requested.
      };

  const handleTimePartKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !e.currentTarget.value) {
      const prev = e.currentTarget.previousElementSibling?.previousElementSibling as HTMLInputElement;
      if (prev) {
        prev.focus();
      }
    }
  };

  const handleTimeBlur =
    (fieldName: "warmupTime" | "cooldownTime") => () => {
      const currentValue = form[fieldName] || "";
      const parts = currentValue.split(":");
      const paddedParts = [
        (parts[0] || "00").padStart(2, "0"),
        (parts[1] || "00").padStart(2, "0"),
        (parts[2] || "00").padStart(2, "0"),
      ];
      const next = paddedParts.join(":");

      const error = validateTime(next);
      setTimeErrors((prev) => {
        if (!error) {
          const { [fieldName]: _, ...rest } = prev;
          return rest;
        }
        return { ...prev, [fieldName]: error };
      });

      setForm((f) => ({ ...f, [fieldName]: next }));
    };

  // RTK mutation hooks
  const [createSession, { isLoading: isSaving }] = useCreateSessionMutation();
  const [editSession, { isLoading: isUpdating }] = useEditSessionMutation();

  // Helper: HH:MM:SS -> total seconds
  const toSeconds = (hhmmss: string) => {
    const parts = hhmmss.split(":").map(Number);
    if (parts.length !== 3 || parts.some(isNaN)) return 0;
    const [h, m, s] = parts;
    return h * 3600 + m * 60 + s;
  };

  // Helper: seconds -> HH:MM:SS
  const secondsToHHMMSS = (secs: number) => {
    const h = Math.floor(secs / 3600)
      .toString()
      .padStart(2, "0");
    const m = Math.floor((secs % 3600) / 60)
      .toString()
      .padStart(2, "0");
    const s = (secs % 60).toString().padStart(2, "0");
    return `${h}:${m}:${s}`;
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitAttempted(true);

    if (
      !form.sessionName ||
      !form.workoutType ||
      !form.warmupTime ||
      Object.keys(timeErrors).length > 0 ||
      !form.cooldownTime
    ) {
      if (!form.warmupTime || form.warmupTime === "00:00:00") {
        setTimeErrors(prev => ({ ...prev, warmupTime: "Required" }));
      }
      if (!form.cooldownTime || form.cooldownTime === "00:00:00") {
        setTimeErrors(prev => ({ ...prev, cooldownTime: "Required" }));
      }
      return;
    }

    const payload = {
      name: form.sessionName,
      category: form.workoutType,
      warmupTime: toSeconds(form.warmupTime),
      cooldownTime: toSeconds(form.cooldownTime),
    };

    try {
      if (sessionid) {
        if (JSON.stringify(form) === JSON.stringify(initialForm)) {
          // No changes made, navigate directly without calling edit API
          navigate(
            `${adminRoutes.sessionFormatBuilder.replace(
              ":sessionid",
              sessionid
            )}?category=${form.workoutType}`
          );
          return;
        }

        await editSession({ id: sessionid, ...payload }).unwrap();
        navigate(
          `${adminRoutes.sessionFormatBuilder.replace(
            ":sessionid",
            sessionid
          )}?category=${form.workoutType}`
        );
      } else {
        const result = await createSession(payload).unwrap();
        const newSessionId = result?.result?._id || result?.data?._id;
        if (newSessionId) {
          navigate(
            `${adminRoutes.sessionFormatBuilder.replace(
              ":sessionid",
              newSessionId
            )}?category=${form.workoutType}`
          );
        } else {
          // Fallback if ID is missing (though should not happen if successful)
          console.error("No session ID returned");
          toast.error("An unexpected error occurred: Missing Session ID");
        }
      }
    } catch (err: any) {
      console.error("Failed to save session", err);
      toast.error(
        err?.data?.message ||
        err?.message ||
        "Failed to save session. Please try again."
      );
    }
  };

  type NavbarSliceState = { isOpen: boolean };
  const isSidebarOpen = useAppSelector(
    (state: RootState) => (state.navbar as NavbarSliceState).isOpen
  );
  const sidebarWidth = isSidebarOpen ? 256 : 64;

  return (
    <div
      className={`px-4 md:px-6 lg:px-8 pt-20 pb-8 min-h-screen ${theme === "dark" ? "bg-[#0B0B0B]" : "bg-gray-50"
        }`}
      style={{
        marginLeft: sidebarWidth,
        width: `calc(100% - ${sidebarWidth}px)`,
      }}
    >
      {/* Header */}
      <div className="mb-6">
        <h1
          className={`text-xl font-semibold ${theme === "dark" ? "text-gray-300" : "text-gray-800"
            }`}
        >
          Sessions Overview
        </h1>
        <div className="flex items-center gap-2 mt-2 text-sm">
          <Link
            to={`${adminRoutes.sessionList}`}
            className={theme === "dark" ? "text-gray-400" : "text-gray-600"}
          >
            All Sessions
          </Link>
          <span
            className={theme === "dark" ? "text-gray-400" : "text-gray-600"}
          >
            /
          </span>
          <span
            className={theme === "dark" ? "text-[#B6CB24]" : "text-lime-600"}
          >
            {sessionid ? "Update Session " : "Create New Session "}
          </span>
        </div>
      </div>
      <form
        onSubmit={onSubmit}
        noValidate
        className={`${cardClass} rounded-lg p-0`}
        style={{ overflow: "hidden" }}
      >
        <div className="flex flex-row ">
          {/* Left: Format Preview */}
          <div
            className={`flex flex-col items-center justify-center min-w-[180px] max-w-[220px] h-[260px] border-r ${theme === "dark"
              ? "bg-[#181818] border-[#232323]"
              : "bg-gray-100 border-gray-300"
              }`}
          >
            <div className="flex flex-col items-center">
              <div
                className={`rounded-xl w-16 h-16 flex items-center justify-center mb-3 ${theme === "dark"
                  ? "bg-[#232323]"
                  : "bg-white border border-gray-200"
                  }`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="36"
                  height="36"
                  viewBox="0 0 36 36"
                  fill="none"
                >
                  <path
                    d="M16.5 31.5H15C9.34314 31.5 6.51473 31.5 4.75736 29.7426C3 27.9853 3 25.1568 3 19.5V15C3 9.34314 3 6.51473 4.75736 4.75736C6.51473 3 9.34314 3 15 3H18C23.6568 3 26.4853 3 28.2426 4.75736C30 6.51473 30 9.34314 30 15V15.75"
                    stroke="#C6D92C"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M26.1103 21.6054C26.4288 20.7982 27.5712 20.7982 27.8896 21.6054L27.9447 21.7453C28.7223 23.7169 30.283 25.2777 32.2546 26.0553L32.3946 26.1103C33.2017 26.4288 33.2017 27.5712 32.3946 27.8896L32.2546 27.9447C30.283 28.7223 28.7223 30.283 27.9447 32.2546L27.8896 32.3946C27.5712 33.2017 26.4288 33.2017 26.1103 32.3946L26.0553 32.2546C25.2777 30.283 23.7169 28.7223 21.7453 27.9447L21.6054 27.8896C20.7982 27.5712 20.7982 26.4288 21.6054 26.1103L21.7453 26.0553C23.7169 25.2777 25.2777 23.7169 26.0553 21.7453L26.1103 21.6054Z"
                    stroke="#C6D92C"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M10.5 10.5H22.5M10.5 17.25H22.5M10.5 24H16.5"
                    stroke="#C6D92C"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <span
                className={`text-sm font-medium text-center ${theme === "dark" ? "text-white" : "text-gray-900"
                  }`}
              >
                Format will appear here
              </span>
            </div>
          </div>
          {/* Right: Form */}
          <div className="flex-1 p-8">
            <div className="mb-6">
              <h2
                className={`text-base font-medium ${theme === "dark" ? "text-white" : "text-gray-800"
                  }`}
              >
                {sessionid ? "Edit Session" : "Create New Session"}
              </h2>
              <div className="w-full h-1 mt-2 rounded bg-[#232323] relative">
                <div
                  className="absolute left-0 top-0 h-1 rounded bg-[#B6CB24]"
                  style={{ width: "180px" }}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              <div>
                <label className={`block text-sm mb-1 ${labelClass}`}>
                  Session Name <span className="text-[#B6CB24]">*</span>
                  {isSubmitAttempted && !form.sessionName && (
                    <span className="text-red-500 text-xs ml-2">
                      Please fill this field
                    </span>
                  )}
                </label>
                <input
                  name="sessionName"
                  value={form.sessionName}
                  onChange={handleChange}
                  placeholder="Enter here"
                  className={inputClass}
                  required
                />
              </div>
              <div>
                <label className={`block text-sm mb-1 ${labelClass}`}>
                  Type of Workout <span className="text-[#B6CB24]">*</span>
                  {isSubmitAttempted && !form.workoutType && (
                    <span className="text-red-500 text-xs ml-2">
                      Please fill this field
                    </span>
                  )}
                </label>
                <select
                  name="workoutType"
                  value={form.workoutType}
                  onChange={handleChange}
                  className={inputClass}
                  required
                >
                  <option value="" disabled>
                    Choose from list
                  </option>
                  {categoryData?.results?.map((cat: any) => (
                    <option key={cat._id} value={cat._id}>
                      {cat.name.replace("_", " ")}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={`block text-sm mb-1 ${labelClass}`}>
                  Warmup Time <span className="text-[#B6CB24]">*</span>
                </label>
                <div
                  className={
                    theme === "dark"
                      ? `w-full flex items-center rounded-md px-3 py-2 transition-colors bg-[#111111] border ${timeErrors.warmupTime ? "border-red-500" : "border-[#2C2C2C] focus-within:border-[#B6CB24]"}`
                      : `w-full flex items-center rounded-md px-3 py-2 transition-colors bg-white border ${timeErrors.warmupTime ? "border-red-500" : "border-gray-300 focus-within:border-[#B6CB24]"}`
                  }
                >
                  <input
                    type="text"
                    value={(form.warmupTime || "00:00:00").split(":")[0] || ""}
                    onChange={handleTimePartChange("warmupTime", 0)}
                    onKeyDown={handleTimePartKeyDown}
                    onBlur={handleTimeBlur("warmupTime")}
                    className={`w-10 text-center bg-transparent outline-none ${theme === "dark" ? "text-gray-200" : "text-gray-800"}`}
                    placeholder="00"
                  />
                  <span className="text-gray-500 font-bold mx-1">:</span>
                  <input
                    type="text"
                    value={(form.warmupTime || "00:00:00").split(":")[1] || ""}
                    onChange={handleTimePartChange("warmupTime", 1)}
                    onKeyDown={handleTimePartKeyDown}
                    onBlur={handleTimeBlur("warmupTime")}
                    className={`w-10 text-center bg-transparent outline-none ${theme === "dark" ? "text-gray-200" : "text-gray-800"}`}
                    placeholder="00"
                  />
                  <span className="text-gray-500 font-bold mx-1">:</span>
                  <input
                    type="text"
                    value={(form.warmupTime || "00:00:00").split(":")[2] || ""}
                    onChange={handleTimePartChange("warmupTime", 2)}
                    onKeyDown={handleTimePartKeyDown}
                    onBlur={handleTimeBlur("warmupTime")}
                    className={`w-10 text-center bg-transparent outline-none ${theme === "dark" ? "text-gray-200" : "text-gray-800"}`}
                    placeholder="00"
                  />
                </div>
                {timeErrors.warmupTime && (
                  <div className="text-red-500 text-xs mt-1">
                    {timeErrors.warmupTime}
                  </div>
                )}
              </div>
              <div>
                <label className={`block text-sm mb-1 ${labelClass}`}>
                  Cool Down Time <span className="text-[#B6CB24]">*</span>
                </label>
                <div
                  className={
                    theme === "dark"
                      ? `w-full flex items-center rounded-md px-3 py-2 transition-colors bg-[#111111] border ${timeErrors.cooldownTime ? "border-red-500" : "border-[#2C2C2C] focus-within:border-[#B6CB24]"}`
                      : `w-full flex items-center rounded-md px-3 py-2 transition-colors bg-white border ${timeErrors.cooldownTime ? "border-red-500" : "border-gray-300 focus-within:border-[#B6CB24]"}`
                  }
                >
                  <input
                    type="text"
                    value={(form.cooldownTime || "00:00:00").split(":")[0] || ""}
                    onChange={handleTimePartChange("cooldownTime", 0)}
                    onKeyDown={handleTimePartKeyDown}
                    onBlur={handleTimeBlur("cooldownTime")}
                    className={`w-10 text-center bg-transparent outline-none ${theme === "dark" ? "text-gray-200" : "text-gray-800"}`}
                    placeholder="00"
                  />
                  <span className="text-gray-500 font-bold mx-1">:</span>
                  <input
                    type="text"
                    value={(form.cooldownTime || "00:00:00").split(":")[1] || ""}
                    onChange={handleTimePartChange("cooldownTime", 1)}
                    onKeyDown={handleTimePartKeyDown}
                    onBlur={handleTimeBlur("cooldownTime")}
                    className={`w-10 text-center bg-transparent outline-none ${theme === "dark" ? "text-gray-200" : "text-gray-800"}`}
                    placeholder="00"
                  />
                  <span className="text-gray-500 font-bold mx-1">:</span>
                  <input
                    type="text"
                    value={(form.cooldownTime || "00:00:00").split(":")[2] || ""}
                    onChange={handleTimePartChange("cooldownTime", 2)}
                    onKeyDown={handleTimePartKeyDown}
                    onBlur={handleTimeBlur("cooldownTime")}
                    className={`w-10 text-center bg-transparent outline-none ${theme === "dark" ? "text-gray-200" : "text-gray-800"}`}
                    placeholder="00"
                  />
                </div>
                {timeErrors.cooldownTime && (
                  <div className="text-red-500 text-xs mt-1">
                    {timeErrors.cooldownTime}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3 justify-center">
              <button
                type="submit"
                disabled={
                  isSaving ||
                  isUpdating ||
                  isFetching ||
                  Object.keys(timeErrors).length > 0 ||
                  (isSubmitAttempted &&
                    (!form.sessionName ||
                      !form.workoutType ||
                      !form.warmupTime ||
                      !form.cooldownTime))
                }
                className={`flex items-center gap-2 px-8 py-1.5 rounded-[10px] border border-[#C6D92C] text-black font-medium hover:opacity-95 disabled:opacity-60 disabled:cursor-not-allowed ${theme === "dark"
                  ? "bg-[#232323] text-gray-200 hover:bg-[#333333]"
                  : "bg-gray-200 text-gray-800 hover:bg-gray-300"
                  }`}
                style={{
                  background:
                    "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.80) 0%, rgba(83, 91, 16, 0.80) 100%)",
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M19 21C19.6068 20.4102 22 18.8403 22 18C22 17.1597 19.6068 15.5898 19 15M21 18H14"
                    stroke="#C6D92C"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 21C7.28595 21 4.92893 21 3.46447 19.5355C2 18.0711 2 15.714 2 11V7.94427C2 6.1278 2 5.21956 2.38032 4.53806C2.65142 4.05227 3.05227 3.65142 3.53806 3.38032C4.21956 3 5.1278 3 6.94427 3C8.10802 3 8.6899 3 9.19926 3.19101C10.3622 3.62712 10.8418 4.68358 11.3666 5.73313L12 7M8 7H16.75C18.8567 7 19.91 7 20.6667 7.50559C20.9943 7.72447 21.2755 8.00572 21.4944 8.33329C21.9796 9.05942 21.9992 10.0588 22 12V13"
                    stroke="#F3F3F6"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {isSaving || isUpdating
                  ? "Saving..."
                  : sessionid
                    ? "Update & Next"
                    : "Save & Next"}
              </button>
              <button
                type="button"
                onClick={() => navigate(adminRoutes.sessionList)}
                className={`flex items-center gap-2 px-5 py-1.5 rounded-md font-medium border border-[#444] ${theme === "dark"
                  ? "bg-[#232323] text-gray-200 hover:bg-[#333333]"
                  : "bg-gray-200 text-gray-800 hover:bg-gray-300"
                  }`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M12 21.5C16.4783 21.5 18.7175 21.5 20.1088 20.1088C21.5 18.7175 21.5 16.4783 21.5 12C21.5 7.52165 21.5 5.28248 20.1088 3.89124C18.7175 2.5 16.4783 2.5 12 2.5C7.52166 2.5 5.28249 2.5 3.89124 3.89124C2.5 5.28249 2.5 7.52166 2.5 12C2.5 16.4783 2.5 18.7175 3.89124 20.1088C5.28248 21.5 7.52165 21.5 12 21.5Z"
                    stroke="#F3F3F6"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M15 9L9 14.9996M15 15L9 9.00039"
                    stroke="#F3F3F6"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Cancel
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default SessionForm;
